import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Search, Filter, RefreshCw, Activity, History, AlertTriangle, CheckCircle, Clock, Eye, Trash2 } from 'lucide-react';
import { useCachedManualCheck } from '@/hooks/useApi';
import { useRegisterRefresh } from '@/contexts/RefreshContext';
import { agentsApi, getApiBaseUrl } from '@/services/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import Modal from '@/components/common/Modal';

type AgentType = 'monitor' | 'verify' | 'execute' | 'learn';

const AgentsPage: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<AgentType>('monitor');
  const [clearingData, setClearingData] = useState(false);
  const [processingFix, setProcessingFix] = useState<string | null>(null); // Track which fix is being processed
  const [showingRejectionForm, setShowingRejectionForm] = useState<string | null>(null); // Track which fix's rejection form is open
  const [rejectionData, setRejectionData] = useState<{[key: string]: {category: string, reason: string}}>({});
  const [modal, setModal] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    details?: string[];
    onConfirm?: () => void;
  }>({ open: false, variant: 'info', message: '' });

  const showModal = (variant: typeof modal.variant, title: string, message: string, details?: string[], onConfirm?: () => void) => {
    setModal({ open: true, variant, title, message, details, onConfirm });
  };

  const API_BASE = getApiBaseUrl();

  // Direct API state for agents data
  const [agentsData, setAgentsData] = useState<{
    allAgents: any;
    agentActivities: any;
    loading: boolean;
    activitiesLoading: boolean;
    error: string | null;
  }>({
    allAgents: null,
    agentActivities: null,
    loading: true,
    activitiesLoading: true,
    error: null
  });

  // Fallback hooks for compatibility
  const { data: allAgents, loading: agentsLoading, error: agentsError, refresh: refreshAgents } = useCachedManualCheck(
    () => agentsApi.listAgents(),
    'agents-list'
  );

  // Agent activities for selected agent - auto-load when selectedAgent changes
  const { data: agentActivities, loading: activitiesLoading, error: activitiesError, refresh: refreshActivities } = useCachedManualCheck(
    () => agentsApi.getAgentActivities(selectedAgent),
    `agent-activities-${selectedAgent}`
  );

  // Direct API loading for agents data (ONLY on selectedAgent change)
  useEffect(() => {
    let isMounted = true;
    
    const loadAgentsData = async () => {
      try {
        setAgentsData(prev => ({ ...prev, loading: true, activitiesLoading: true }));
        
        const [agents, activities] = await Promise.allSettled([
          agentsApi.listAgents(),
          agentsApi.getAgentActivities(selectedAgent)
        ]);
        
        if (!isMounted) return; // Prevent state updates if unmounted
        
        setAgentsData({
          allAgents: agents.status === 'fulfilled' ? agents.value : null,
          agentActivities: activities.status === 'fulfilled' ? activities.value : null,
          loading: false,
          activitiesLoading: false,
          error: null
        });
        
      } catch (error) {
        console.error('Agents data loading error:', error);
        if (!isMounted) return;
        
        setAgentsData(prev => ({
          ...prev,
          loading: false,
          activitiesLoading: false,
          error: 'Failed to load agents data'
        }));
      }
    };
    
    loadAgentsData();
    
    return () => {
      isMounted = false;
    };
  }, [selectedAgent]);

  // Manual refresh function (memoized to prevent re-registration loops)
  const manualRefresh = useCallback(async () => {
    try {
      setAgentsData(prev => ({ ...prev, loading: true, activitiesLoading: true }));
      
      const [agents, activities] = await Promise.allSettled([
        agentsApi.listAgents(),
        agentsApi.getAgentActivities(selectedAgent)
      ]);
      
      setAgentsData({
        allAgents: agents.status === 'fulfilled' ? agents.value : null,
        agentActivities: activities.status === 'fulfilled' ? activities.value : null,
        loading: false,
        activitiesLoading: false,
        error: null
      });
      
    } catch (error) {
      console.error('Manual refresh error:', error);
      setAgentsData(prev => ({
        ...prev,
        loading: false,
        activitiesLoading: false,
        error: 'Failed to refresh agents data'
      }));
    }
  }, [selectedAgent]);

  // Register refresh handlers
  useRegisterRefresh('agents-all', manualRefresh);

  // Use direct data, fallback to hook data
  const finalAllAgents = agentsData.allAgents || allAgents;
  const finalAgentActivities = agentsData.agentActivities || agentActivities;

  const agents = [
    {
      key: 'monitor' as AgentType,
      name: 'Monitor Agent',
      icon: Eye,
      color: 'bg-blue-100 text-blue-800',
      description: 'Monitors system health and detects issues',
      status: finalAllAgents?.data?.agents?.find((a: any) => a.type === 'monitor')?.status || 'active'
    },
    {
      key: 'verify' as AgentType,
      name: 'Verify Agent',
      icon: CheckCircle,
      color: 'bg-green-100 text-green-800',
      description: 'Verifies data consistency across systems',
      status: finalAllAgents?.data?.agents?.find((a: any) => a.type === 'verify')?.status || 'active'
    },
    {
      key: 'execute' as AgentType,
      name: 'Execute Agent',
      icon: Bot,
      color: 'bg-purple-100 text-purple-800',
      description: 'Executes corrective actions automatically',
      status: finalAllAgents?.data?.agents?.find((a: any) => a.type === 'execute')?.status || 'active'
    },
    {
      key: 'learn' as AgentType,
      name: 'Learn Agent',
      icon: Activity,
      color: 'bg-orange-100 text-orange-800',
      description: 'Learns from patterns and improves detection',
      status: finalAllAgents?.data?.agents?.find((a: any) => a.type === 'learn')?.status || 'active'
    }
  ];

  const refreshAllData = async () => {
    try {
      setAgentsData(prev => ({ ...prev, loading: true, activitiesLoading: true }));
      
      const [agents, activities] = await Promise.allSettled([
        agentsApi.listAgents(),
        agentsApi.getAgentActivities(selectedAgent)
      ]);
      
      setAgentsData({
        allAgents: agents.status === 'fulfilled' ? agents.value : null,
        agentActivities: activities.status === 'fulfilled' ? activities.value : null,
        loading: false,
        activitiesLoading: false,
        error: null
      });
      
      // Also refresh hooks as backup
    refreshAgents();
    refreshActivities();
    } catch (error) {
      console.error('Agents refresh error:', error);
      setAgentsData(prev => ({
        ...prev,
        loading: false,
        activitiesLoading: false,
        error: 'Failed to refresh agents data'
      }));
    }
  };

  const clearAllAgentData = async () => {
    setClearingData(true);
    try {
      await agentsApi.clearAllActivities();
      // Refresh after clearing to show empty state
      refreshAgents();
      refreshActivities();
    } catch (error) {
      console.error('Failed to clear agent data:', error);
      showModal('error', 'Clear Failed', 'Failed to clear agent data. Please try again.');
    } finally {
      setClearingData(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': 
      case 'active': return 'bg-green-100 text-green-800';
      case 'idle': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityCount = (agentType: AgentType) => {
    return finalAgentActivities?.data?.activities?.length || 0;
  };

  // Combined loading and error states
  const isLoading = agentsData.loading || agentsData.activitiesLoading || (agentsLoading && activitiesLoading);
  const hasError = agentsData.error || agentsError || activitiesError;

  return (
    <div className="flex h-full">
      {/* Left Panel - Agents */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">AI Agents</h2>
          <p className="text-sm text-gray-600">Select agent to view activities</p>
        </div>
        
        <div className="flex-1 p-4">
          <div className="space-y-2">
            {agents.map((agent) => {
              const Icon = agent.icon;
              const isActive = selectedAgent === agent.key;
              
              return (
                <button
                  key={agent.key}
                  onClick={() => setSelectedAgent(agent.key)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-blue-100 border-2 border-blue-500 text-blue-900' 
                      : 'bg-white border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon className="h-5 w-5 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{agent.name}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                          {agent.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-tight">{agent.description}</p>
                      {isActive && (
                        <p className="text-xs text-blue-600 mt-1">
                          {getActivityCount(agent.key)} recent activities
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
            <button
              onClick={refreshAllData}
              className="w-full btn btn-outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh All
            </button>
            
            <button
              onClick={clearAllAgentData}
              disabled={clearingData}
              className="w-full btn btn-outline text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className={`h-4 w-4 mr-2 ${clearingData ? 'animate-pulse' : ''}`} />
              {clearingData ? 'Clearing...' : 'Clear All Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel - Agent Activities */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {agents.find(a => a.key === selectedAgent)?.name}
              </h1>
              <p className="text-gray-600">
                {agents.find(a => a.key === selectedAgent)?.description}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agents.find(a => a.key === selectedAgent)?.status || 'unknown')}`}>
                {agents.find(a => a.key === selectedAgent)?.status || 'unknown'}
              </span>
              <button
                onClick={refreshActivities}
                className="btn btn-outline"
                disabled={activitiesLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${activitiesLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Agent metrics removed - use Dashboard for system-wide metrics */}
        </div>

        {/* Agent Activities */}
        <div className="flex-1 p-6">
          {(agentsData.activitiesLoading || activitiesLoading) && !finalAgentActivities ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner text={`Loading ${agents.find(a => a.key === selectedAgent)?.name} activities...`} />
            </div>
          ) : (agentsData.error || activitiesError) && !finalAgentActivities ? (
            <div className="flex items-center justify-center h-64">
              <ErrorMessage message={`Failed to load ${agents.find(a => a.key === selectedAgent)?.name} activities`} onRetry={refreshAllData} />
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                Recent Activities ({finalAgentActivities?.data?.activities?.length || 0})
              </h3>
              
              {finalAgentActivities?.data?.activities?.length ? (
                <div className="space-y-3">
                  {finalAgentActivities.data.activities.map((activity: any, index: number) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          {activity.type === 'detection' ? (
                            <Eye className="h-5 w-5 text-blue-500" />
                          ) : activity.type === 'verification' ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : activity.type === 'execution' ? (
                            <Bot className="h-5 w-5 text-purple-500" />
                          ) : activity.type === 'learning' ? (
                            <Activity className="h-5 w-5 text-orange-500" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900">{activity.title || 'Agent Activity'}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                              activity.status === 'failed' ? 'bg-red-100 text-red-800' :
                              activity.status === 'running' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {activity.status || 'unknown'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {activity.description || 'No description available'}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>{activity.timestamp || 'Unknown time'}</span>
                            <span>{activity.system || 'Unknown system'}</span>
                            {activity.duration && <span>Duration: {activity.duration}</span>}
                          </div>
                        {/* ENHANCED: Show detailed analysis from API response */}
                        <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                          <div className="space-y-2 text-sm">
                            
                            {/* AI Analysis Section */}
                            {activity.details?.ai_reasoning && (
                              <div className="bg-white p-2 rounded border-l-4 border-blue-500">
                                <div className="font-semibold text-blue-900">🤖 AI Analysis:</div>
                                <div className="text-blue-800">{activity.details.ai_reasoning}</div>
                              </div>
                            )}
                            
                            {/* System Analysis Section */}
                            {activity.metadata && (
                              <div className="bg-white p-2 rounded border-l-4 border-green-500">
                                <div className="font-semibold text-green-900">🔍 System Analysis:</div>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                  <div><strong>Order:</strong> {activity.metadata.order_id || 'N/A'}</div>
                                </div>
                                
                                {/* ENHANCED: Real Price Fetching Table for Verify Agent */}
                                {activity.type === 'verify' && activity.details?.systems_analyzed_details && (
                                  <div className="mt-3">
                                    <div className="font-semibold text-sm text-green-800 mb-2">📊 Real Data Fetched from Systems:</div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs border border-gray-200 rounded">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-3 py-1 text-left border-b font-medium">System</th>
                                            <th className="px-3 py-1 text-left border-b font-medium">Order Found</th>
                     <th className="px-3 py-1 text-left border-b font-medium">Total Amount</th>
                     <th className="px-3 py-1 text-left border-b font-medium">Tax Amount</th>
                     <th className="px-3 py-1 text-left border-b font-medium">Subtotal</th>
                     <th className="px-3 py-1 text-left border-b font-medium">Status</th>
                     <th className="px-3 py-1 text-left border-b font-medium">Customer</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {activity.details.systems_analyzed_details.map((systemData: any, idx: number) => {
                                            const isGKPOS = systemData.system === 'GKPOS';
                                            const isSAPECC = systemData.system === 'SAP_ECC';
                                            const isHybris = systemData.system === 'HYBRIS';
                                            const totalAmount = systemData.total_amount || 'N/A';
                                            const taxAmount = systemData.tax_amount || 'N/A';
                                            const subtotal = systemData.subtotal || 'N/A';
                                            const status = systemData.status || 'N/A';
                                            const customerName = systemData.customer_name || 'N/A';
                                            const orderFound = systemData.order_found;
                                            
                                            return (
                                              <tr key={idx} className={idx < activity.details.systems_analyzed_details.length - 1 ? 'border-b' : ''}>
                                                <td className="px-3 py-1 font-medium">{systemData.system}</td>
                                                <td className="px-3 py-1">
                                                  {orderFound ? (
                                                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">✅ Found</span>
                                                  ) : (
                                                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">❌ Not Found</span>
                                                  )}
                                                </td>
                                                <td className={`px-3 py-1 font-mono ${orderFound ? '' : 'text-gray-400'}`}>
                                                  {totalAmount}
                                                </td>
                                                <td className={`px-3 py-1 ${orderFound ? '' : 'text-gray-400'}`}>
                                                  {taxAmount}
                                                </td>
                                                <td className={`px-3 py-1 ${orderFound ? '' : 'text-gray-400'}`}>
                                                  {subtotal}
                                                </td>
                                                <td className={`px-3 py-1 ${orderFound ? '' : 'text-gray-400'}`}>
                                                  {status}
                                                </td>
                                                <td className={`px-3 py-1 ${orderFound ? '' : 'text-gray-400'}`}>
                                                  {customerName}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    {activity.details.field_by_field_analysis && (
                                      <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                                        <span className="font-semibold text-yellow-800">⚠️ Price Discrepancy Detected:</span>
                                        {activity.details.field_by_field_analysis
                                          .filter((field: any) => !field.is_consistent && field.field_name === 'total_amount')
                                          .map((field: any, idx: number) => (
                                            <span key={idx} className="text-yellow-700"> 
                                              {Object.entries(field.system_values || {}).map(([sys, val]) => `${sys} ($${val})`).join(' vs ')}
                                            </span>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Dynamic Recommendations from API */}
                                {(activity.details?.top_recommendations || activity.metadata?.recommendations) && (
                                  <div className="mt-2">
                                    <div className="font-medium">💡 Recommendations:</div>
                                    <ul className="list-disc list-inside text-xs">
                                      {(activity.details?.top_recommendations || activity.metadata?.recommendations || [])
                                        .slice(0, 3)
                                        .map((rec: any, idx: number) => (
                                          <li key={idx}>{typeof rec === 'string' ? rec : rec.action || rec.description || 'Apply fix'}</li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            
                            {/* Technical Details Section - DYNAMIC */}
                            <div className="bg-white p-2 rounded border-l-4 border-orange-500">
                              <div className="font-semibold text-orange-900">⚙️ Technical Details:</div>
                              <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
                                <div><strong>Workflow ID:</strong> {activity.metadata?.workflow_id?.split('-').slice(-2).join('-') || `#${activity.id}` || 'N/A'}</div>
                                <div><strong>Agent Type:</strong> {activity.type?.toUpperCase() || 'PROCESSING'}</div>
                                <div><strong>Status:</strong> <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">{activity.status}</span></div>
                                <div><strong>Timestamp:</strong> {new Date(activity.timestamp).toLocaleTimeString()}</div>
                                {activity.details?.discrepancies_found !== undefined && (
                                  <div><strong>Discrepancies:</strong> <span className="text-red-600 font-bold">{activity.details.discrepancies_found}</span></div>
                                )}
                                {activity.details?.analysis_timestamp && (
                                  <div><strong>Analysis At:</strong> {new Date(activity.details.analysis_timestamp).toLocaleTimeString()}</div>
                                )}
                              </div>
                            </div>
                            
                            {/* Next Steps / Execute Agent Fixes */}
                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-2 rounded border border-emerald-200">
                              <div className="text-xs mt-1">
                                {activity.details?.next_steps && activity.details.next_steps.length > 0 && (
                                  <div className="mb-2">
                                    <div className="font-semibold text-emerald-900 mb-1">🎯 Next Steps:</div>
                                    <ul className="list-disc list-inside space-y-1">
                                      {activity.details.next_steps.map((step: string, idx: number) => (
                                        <li key={idx}>{step}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {activity.type === 'execute' && (
                                  <div className="space-y-3">
                                    <div className="text-sm font-semibold text-emerald-800">🛠️ Execute Agent - Proposed Fixes:</div>
                                    
                                    {/* Dynamic Fix Commands from API */}
                                    {activity.metadata?.fix_commands && activity.metadata.fix_commands.length > 0 ? (
                                      <div className="space-y-2">
                                        {activity.metadata.fix_commands.map((fix: any, idx: number) => (
                                          <div key={idx} className={`border rounded p-3 ${
                                            fix.status === 'applied' ? 'border-green-300 bg-green-50' :
                                            fix.status === 'rejected' ? 'border-red-300 bg-red-50' :
                                            'border-blue-200 bg-blue-50'
                                          }`}>
                                            <div className="space-y-2">
                                              {/* Fix Header */}
                                              <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="font-medium text-blue-800 text-sm">
                                                      {fix.discrepancy_type ? `📊 ${fix.field_name || fix.discrepancy_type}` : `${fix.target_system} Sync`}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                      fix.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                                      fix.priority === 'high' ? 'bg-orange-100 text-orange-700' : 
                                                      'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                      {fix.priority} priority
                                                    </span>
                                                    {fix.status && (
                                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        fix.status === 'applied' ? 'bg-green-100 text-green-700' :
                                                        fix.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-100 text-gray-700'
                                                      }`}>
                                                        {fix.status}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {/* Show affected systems for new structure */}
                                                  {fix.affected_systems && fix.affected_systems.length > 0 && (
                                                    <div className="flex items-center space-x-2 mt-2">
                                                      <span className="text-xs text-gray-600">Affects:</span>
                                                      {fix.affected_systems.map((system: string, sidx: number) => (
                                                        <span key={sidx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                                          {system}
                                                        </span>
                                                      ))}
                                                      <span className="text-xs text-gray-500">← from {fix.authoritative_system}</span>
                                                    </div>
                                                  )}
                                                  <div className="text-blue-700 text-xs mt-1">
                                                    {fix.reason}
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Changes Summary */}
                                              <div className="bg-white border border-blue-100 rounded p-2">
                                                <div className="font-semibold text-gray-700 text-xs mb-1">📝 Changes:</div>
                                                <ul className="text-xs text-gray-600 space-y-0.5">
                                                  {fix.changes_summary?.map((change: string, cidx: number) => (
                                                    <li key={cidx}>• {change}</li>
                                                  ))}
                                                </ul>
                                              </div>

                                              {/* API Details (Expandable) */}
                                              <details className="text-xs">
                                                <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                                                  📄 View API Calls
                                                </summary>
                                                <div className="bg-gray-800 text-green-400 p-2 mt-1 rounded font-mono overflow-x-auto space-y-2">
                                                  {/* New structure: multiple API calls */}
                                                  {fix.api_calls && fix.api_calls.length > 0 ? (
                                                    fix.api_calls.map((call: any, callIdx: number) => (
                                                      <div key={callIdx} className="border-t border-gray-600 pt-2 first:border-t-0 first:pt-0">
                                                        <div className="text-yellow-400 font-semibold">{call.system}</div>
                                                        <div className="text-blue-400">{call.http_method} {call.api_endpoint}</div>
                                                        <pre className="mt-1 text-xs">{JSON.stringify(call.payload, null, 2)}</pre>
                                                      </div>
                                                    ))
                                                  ) : (
                                                    /* Old structure: single API call */
                                                    <div>
                                                      <div className="text-blue-400">{fix.http_method} {fix.api_endpoint}</div>
                                                      <pre className="mt-1">{JSON.stringify(fix.payload, null, 2)}</pre>
                                                    </div>
                                                  )}
                                                </div>
                                              </details>

                                              {/* Authoritative Sources Used */}
                                              <div className="flex items-center space-x-2 text-xs">
                                                <span className="text-gray-600">Authoritative sources:</span>
                                                {fix.authoritative_sources_used?.map((source: string, sidx: number) => (
                                                  <span key={sidx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
                                                    {source.toUpperCase()}
                                                  </span>
                                                ))}
                                              </div>

                                              {/* Action Buttons or Status Display */}
                                              {fix.status === 'pending_approval' && (
                                                <div className="pt-2 border-t border-blue-200 space-y-2">
                                                  {/* Action Buttons */}
                                                  <div className="flex space-x-2">
                                                    <button 
                                                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                      disabled={processingFix === `${activity.id}-${idx}` || showingRejectionForm === `${activity.id}-${idx}`}
                                                      onClick={() => {
                                                        const fixKey = `${activity.id}-${idx}`;
                                                        const systems = fix.affected_systems ? fix.affected_systems.join(', ') : fix.target_system;
                                                        const discrepancy = fix.discrepancy_type ? `"${fix.field_name}"` : 'this issue';
                                                        showModal(
                                                          'confirm',
                                                          'Apply Fix',
                                                          `Apply fix for ${discrepancy} to ${systems}?`,
                                                          fix.changes_summary || [],
                                                          async () => {
                                                            try {
                                                              setProcessingFix(fixKey);
                                                              const response = await fetch(`${API_BASE}/agents/execute/apply-fix`, {
                                                                method: 'POST',
                                                                headers: { 'Content-Type': 'application/json' },
                                                                body: JSON.stringify({ activity_id: activity.metadata?.workflow_id || String(activity.id), fix_index: idx })
                                                              });
                                                              const result = await response.json();
                                                              if (result.success) {
                                                                showModal('success', 'Fix Applied', result.message);
                                                                refreshActivities();
                                                              } else {
                                                                showModal('error', 'Fix Failed', result.message);
                                                              }
                                                            } catch (error) {
                                                              showModal('error', 'Error', error instanceof Error ? error.message : 'Unknown error');
                                                            } finally {
                                                              setProcessingFix(null);
                                                            }
                                                          }
                                                        );
                                                      }}
                                                    >
                                                      {processingFix === `${activity.id}-${idx}` ? '⏳ Applying...' : '✅ Apply Fix'}
                                                    </button>
                                                    <button 
                                                      className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-2 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                      disabled={processingFix === `${activity.id}-${idx}` || showingRejectionForm === `${activity.id}-${idx}`}
                                                      onClick={() => {
                                                        const fixKey = `${activity.id}-${idx}`;
                                                        setShowingRejectionForm(fixKey);
                                                      }}
                                                    >
                                                      ❌ Reject Fix
                                                    </button>
                                                  </div>

                                                  {/* Rejection Form */}
                                                  {showingRejectionForm === `${activity.id}-${idx}` && (
                                                    <div className="bg-red-50 border border-red-200 p-3 rounded-md space-y-3">
                                                      <div className="font-semibold text-red-800 text-sm">Why is this fix incorrect?</div>
                                                      
                                                      <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Rejection Category</label>
                                                        <select
                                                          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                          value={rejectionData[`${activity.id}-${idx}`]?.category || ''}
                                                          onChange={(e) => {
                                                            const fixKey = `${activity.id}-${idx}`;
                                                            setRejectionData({
                                                              ...rejectionData,
                                                              [fixKey]: {
                                                                ...rejectionData[fixKey],
                                                                category: e.target.value
                                                              }
                                                            });
                                                          }}
                                                        >
                                                          <option value="">Select a category...</option>
                                                          <option value="wrong_authoritative_source">Wrong Authoritative Source</option>
                                                          <option value="incorrect_data_mapping">Incorrect Data Mapping</option>
                                                          <option value="business_logic_error">Business Logic Error</option>
                                                          <option value="timing_issue">Timing/Race Condition</option>
                                                          <option value="permission_issue">Permission/Access Issue</option>
                                                          <option value="other">Other</option>
                                                        </select>
                                                      </div>

                                                      <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Detailed Explanation</label>
                                                        <textarea
                                                          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                          rows={4}
                                                          placeholder="Explain why this fix is incorrect and what the agent should learn..."
                                                          value={rejectionData[`${activity.id}-${idx}`]?.reason || ''}
                                                          onChange={(e) => {
                                                            const fixKey = `${activity.id}-${idx}`;
                                                            setRejectionData({
                                                              ...rejectionData,
                                                              [fixKey]: {
                                                                ...rejectionData[fixKey],
                                                                reason: e.target.value
                                                              }
                                                            });
                                                          }}
                                                        />
                                                      </div>

                                                      <div className="flex space-x-2">
                                                        <button
                                                          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-2 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                          disabled={processingFix === `${activity.id}-${idx}` || !rejectionData[`${activity.id}-${idx}`]?.category || !rejectionData[`${activity.id}-${idx}`]?.reason}
                                                          onClick={async () => {
                                                            const fixKey = `${activity.id}-${idx}`;
                                                            const data = rejectionData[fixKey];
                                                            
                                                            try {
                                                              setProcessingFix(fixKey);
                                                              const response = await fetch(`${API_BASE}/agents/execute/reject-fix`, {
                                                                method: 'POST',
                                                                headers: { 'Content-Type': 'application/json' },
                                                                body: JSON.stringify({
                                                                  activity_id: activity.metadata?.workflow_id || String(activity.id),
                                                                  fix_index: idx,
                                                                  rejection_category: data.category,
                                                                  reason: data.reason,
                                                                  user_notes: data.reason
                                                                })
                                                              });
                                                              const result = await response.json();
                                                              if (result.success) {
                                                                showModal('success', 'Feedback Recorded', 'Rejection feedback recorded. Learning Agent will use this to improve future decisions.');
                                                                setShowingRejectionForm(null);
                                                                refreshActivities();
                                                              } else {
                                                                showModal('error', 'Submission Failed', `Failed to record feedback: ${result.message}`);
                                                              }
                                                            } catch (error) {
                                                              showModal('error', 'Error', error instanceof Error ? error.message : 'Unknown error');
                                                            } finally {
                                                              setProcessingFix(null);
                                                            }
                                                          }}
                                                        >
                                                          {processingFix === `${activity.id}-${idx}` ? '⏳ Submitting...' : '📤 Submit Rejection'}
                                                        </button>
                                                        <button
                                                          className="px-4 bg-gray-300 hover:bg-gray-400 text-gray-800 text-xs px-3 py-2 rounded-md transition-colors font-medium"
                                                          onClick={() => {
                                                            setShowingRejectionForm(null);
                                                          }}
                                                        >
                                                          Cancel
                                                        </button>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                              
                                              {/* Status Display After Action */}
                                              {fix.status === 'applied' && (
                                                <div className="bg-green-50 border border-green-300 p-3 rounded-md pt-2 border-t">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="text-green-600 font-bold text-lg">✅</span>
                                                    <div className="flex-1">
                                                      <div className="font-semibold text-green-800 text-sm">Fix Applied Successfully</div>
                                                      <div className="text-green-600 text-xs mt-0.5">
                                                        {fix.applied_at && `Applied at ${new Date(fix.applied_at).toLocaleString()}`}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              {fix.status === 'rejected' && (
                                                <div className="bg-red-50 border border-red-300 p-3 rounded-md pt-2 border-t space-y-2">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="text-red-600 font-bold text-lg">❌</span>
                                                    <div className="flex-1">
                                                      <div className="font-semibold text-red-800 text-sm">Fix Rejected</div>
                                                      <div className="text-red-600 text-xs mt-0.5">
                                                        {fix.rejected_at && `Rejected at ${new Date(fix.rejected_at).toLocaleString()}`}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* User Feedback Display */}
                                                  {fix.rejection_feedback && (
                                                    <div className="bg-white border border-red-200 p-2 rounded text-xs space-y-1.5">
                                                      <div className="font-semibold text-red-900">📝 User Feedback:</div>
                                                      <div>
                                                        <span className="font-medium text-gray-700">Category:</span>{' '}
                                                        <span className="text-red-700">
                                                          {fix.rejection_feedback.category?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                                        </span>
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-gray-700">Explanation:</span>
                                                        <div className="text-gray-800 mt-0.5 italic bg-gray-50 p-2 rounded border border-gray-200">
                                                          "{fix.rejection_feedback.reason}"
                                                        </div>
                                                      </div>
                                                      <div className="text-purple-600 text-xs mt-2 pt-2 border-t border-red-200">
                                                        🧠 <strong>Learning Agent:</strong> This feedback has been saved to the knowledge bank and will improve future fix recommendations.
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="bg-gray-50 border border-gray-200 p-3 rounded text-xs text-gray-600">
                                        No fix commands available. Execute Agent may still be analyzing...
                                      </div>
                                    )}
                                  </div>
                                )}
                                {activity.type === 'learn' && (
                                  <ul className="list-disc list-inside space-y-1">
                                    <li>📚 Pattern learned and stored in Knowledge Bank</li>
                                    <li>🧠 Enhanced future incident detection</li>
                                    <li>⚡ 90% faster resolution for similar cases</li>
                                  </ul>
                                )}
                              </div>
                            </div>
                            
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bot className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="text-gray-500">No activities found for {agents.find(a => a.key === selectedAgent)?.name}</p>
                  <p className="text-sm text-gray-400 mt-1">Activities will appear here when the agent performs actions</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        details={modal.details}
        onConfirm={modal.onConfirm}
        confirmLabel={modal.variant === 'confirm' ? 'Apply Fix' : undefined}
      />
    </div>
  );
};

export default AgentsPage;